import { motion } from 'framer-motion';
import { Shield, ArrowLeft, Compass } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Decorative top border */}
      <div className="h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400" />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-400 rounded-xl blur-md opacity-30" />
                <div className="relative w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Compass className="w-8 h-8 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-display font-bold text-blue-900 tracking-tight">
                  Masonic Tools
                </h1>
                <p className="text-sm text-gray-500 font-serif italic">Professional Unit Management</p>
              </div>
            </div>
            <Link
              to="/"
              className="flex items-center gap-2 px-6 py-3 bg-blue-900 text-white font-semibold rounded-xl hover:bg-blue-800 transition-all duration-300 hover:shadow-lg"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white px-4 sm:px-6 lg:px-8 py-16 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-blue-900 rounded-full blur-xl opacity-20" />
              <div className="relative p-6 bg-blue-900 rounded-full shadow-2xl">
                <Shield className="w-12 h-12 text-white" />
              </div>
            </div>
            <h2 className="text-5xl md:text-6xl font-display font-bold mb-6 text-blue-900 leading-tight tracking-tight">
              Privacy Policy
            </h2>
            <p className="text-xl text-gray-600 mb-4 font-serif">
              Your privacy is important to us. This policy outlines how we collect, use, and protect your information.
            </p>
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <div className="w-12 h-px bg-gray-300" />
              <Compass className="w-4 h-4" />
              <div className="w-12 h-px bg-gray-300" />
            </div>
            <p className="text-sm text-gray-500 mt-4">Last Updated: October 9, 2025</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-white px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed mb-0">
                This Privacy Policy applies to all Masonic Tools applications, including Dining, Secretary, Treasurer, and any future applications within the Masonic Tools suite.
              </p>
            </div>

            <div className="space-y-12">
              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">1.</span> Introduction
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Masonic Tools ("we," "our," or "us") is committed to protecting the privacy and security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our suite of applications designed for Masonic unit management.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">2.</span> Information We Collect
                </h3>
                <div className="ml-8 space-y-4">
                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">2.1 Personal Information</h4>
                    <p className="text-gray-700 leading-relaxed mb-2">We may collect personal information that you provide directly to us, including but not limited to:</p>
                    <ul className="list-disc ml-6 text-gray-700 space-y-2">
                      <li>Name, email address, and contact information</li>
                      <li>Unit membership details and rank</li>
                      <li>Payment information for event registrations and subscriptions</li>
                      <li>Dietary requirements and preferences for event planning</li>
                      <li>Profile information and preferences</li>
                      <li>Communication history with other members or administrators</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">2.2 Automatically Collected Information</h4>
                    <p className="text-gray-700 leading-relaxed mb-2">When you use our applications, we may automatically collect:</p>
                    <ul className="list-disc ml-6 text-gray-700 space-y-2">
                      <li>Log data including IP address, browser type, and operating system</li>
                      <li>Device information and unique identifiers</li>
                      <li>Usage data and analytics information</li>
                      <li>Cookies and similar tracking technologies</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">2.3 Unit Data</h4>
                    <p className="text-gray-700 leading-relaxed mb-2">Information related to unit operations, including:</p>
                    <ul className="list-disc ml-6 text-gray-700 space-y-2">
                      <li>Event details, RSVPs, and attendance records</li>
                      <li>Financial transactions and records</li>
                      <li>Meeting minutes and administrative documents</li>
                      <li>Member directories and contact information</li>
                      <li>Unit settings and configurations</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">3.</span> How We Use Your Information
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">We use the collected information for the following purposes:</p>
                <ul className="list-disc ml-6 text-gray-700 space-y-2">
                  <li>To provide, operate, and maintain our applications</li>
                  <li>To process transactions and manage event registrations</li>
                  <li>To send administrative information, updates, and notifications</li>
                  <li>To respond to your inquiries and provide customer support</li>
                  <li>To improve and personalize your experience</li>
                  <li>To analyze usage trends and optimize our services</li>
                  <li>To detect, prevent, and address technical issues or security threats</li>
                  <li>To comply with legal obligations and enforce our terms</li>
                </ul>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">4.</span> Information Sharing and Disclosure
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">We may share your information in the following circumstances:</p>
                <div className="ml-8 space-y-4">
                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">4.1 Within Your Unit</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Information you provide may be shared with other members and administrators of your unit as necessary for unit operations and communications.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">4.2 Service Providers</h4>
                    <p className="text-gray-700 leading-relaxed">
                      We may share information with third-party service providers who perform services on our behalf, including:
                    </p>
                    <ul className="list-disc ml-6 text-gray-700 space-y-2 mt-2">
                      <li>Payment processors (Stripe) for handling transactions</li>
                      <li>Cloud hosting services (Firebase/Google Cloud Platform)</li>
                      <li>Email service providers for communications</li>
                      <li>Analytics providers to improve our services</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">4.3 Legal Requirements</h4>
                    <p className="text-gray-700 leading-relaxed">
                      We may disclose your information if required by law, legal process, or government request, or to protect the rights, property, or safety of Masonic Tools, our users, or others.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">4.4 Business Transfers</h4>
                    <p className="text-gray-700 leading-relaxed">
                      If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">5.</span> Data Security
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We implement appropriate technical and organizational security measures to protect your information, including:
                </p>
                <ul className="list-disc ml-6 text-gray-700 space-y-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure authentication mechanisms including two-factor authentication</li>
                  <li>Role-based access controls to limit data access</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>Secure cloud infrastructure with enterprise-grade protection</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">6.</span> Data Retention
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When you delete your account or request data deletion, we will delete or anonymize your information within a reasonable timeframe, subject to legal and operational requirements.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">7.</span> Your Rights and Choices
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">Depending on your location, you may have the following rights regarding your personal information:</p>
                <ul className="list-disc ml-6 text-gray-700 space-y-2">
                  <li><strong>Access:</strong> Request access to your personal information</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong>Portability:</strong> Request a copy of your information in a portable format</li>
                  <li><strong>Objection:</strong> Object to the processing of your information</li>
                  <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  To exercise these rights, please contact us using the information provided at the end of this policy.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">8.</span> Cookies and Tracking Technologies
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We use cookies and similar tracking technologies to track activity on our applications and hold certain information. You can instruct your browser to refuse all cookies or indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some features of our applications.
                </p>
                <div className="ml-8 space-y-3 mt-3">
                  <div>
                    <h4 className="text-lg font-semibold text-blue-800 mb-1">Essential Cookies</h4>
                    <p className="text-gray-700 leading-relaxed">Required for the operation of our applications, including authentication and security.</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-800 mb-1">Analytics Cookies</h4>
                    <p className="text-gray-700 leading-relaxed">Help us understand how users interact with our applications.</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-800 mb-1">Functional Cookies</h4>
                    <p className="text-gray-700 leading-relaxed">Remember your preferences and settings.</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">9.</span> Third-Party Services
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Our applications may contain links to third-party websites or integrate with third-party services, including:
                </p>
                <ul className="list-disc ml-6 text-gray-700 space-y-2">
                  <li>Stripe for payment processing</li>
                  <li>Google Firebase for authentication and data storage</li>
                  <li>Email service providers</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">10.</span> Children's Privacy
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Our applications are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">11.</span> International Data Transfers
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Your information may be transferred to and maintained on servers located outside of your jurisdiction where data protection laws may differ. By using our applications, you consent to the transfer of your information to countries outside of your residence, including the United States and the United Kingdom.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">12.</span> Changes to This Privacy Policy
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically for any changes. Changes become effective when posted.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">13.</span> Contact Us
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <p className="text-gray-700 leading-relaxed mb-2">
                    <strong className="text-blue-900">Masonic Tools</strong>
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    Email: <a href="mailto:privacy@masonic-tools.co.uk" className="text-blue-600 hover:text-blue-800 underline">privacy@masonic-tools.co.uk</a>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Website: <a href="https://masonic-tools.co.uk" className="text-blue-600 hover:text-blue-800 underline">https://masonic-tools.co.uk</a>
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-900 p-6 rounded-r-lg mt-8">
                <p className="text-gray-700 leading-relaxed mb-0">
                  <strong className="text-blue-900">Acknowledgment:</strong> By using Masonic Tools applications, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 border-t border-blue-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-400 rounded-xl blur-md opacity-30" />
                <div className="relative w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Compass className="w-7 h-7 text-white" />
                </div>
              </div>
              <div>
                <p className="text-white font-display font-bold text-xl">Masonic Tools</p>
                <p className="text-sm text-blue-200 font-serif italic">Professional Unit Management</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-blue-100 font-medium">© {new Date().getFullYear()} Masonic Tools. All rights reserved.</p>
              <div className="flex gap-4 mt-2 justify-center md:justify-end">
                <Link to="/privacy" className="text-sm text-blue-300 hover:text-yellow-400 transition-colors">Privacy Policy</Link>
                <span className="text-blue-300">•</span>
                <Link to="/terms" className="text-sm text-blue-300 hover:text-yellow-400 transition-colors">Terms & Conditions</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;

